import { defineMetrics } from "../types.js"

/**
 * 1. **`ProductChecklist` has no `status` field** (the same trap as `application_status`).
 * srv's own checklist dashboard derives it from two booleans, `approved` and `docsSubmitted`,
 * and we follow that.
 * ⚠ But srv's three components are a **nested ladder, not a partition**:
 * `getSubmittedChecklists` is `approved && docsSubmitted`, so it is entirely contained in
 * `getApprovedChecklists`. A pie needs mutually exclusive slices, so this subtracts:
 * approved-not-submitted / submitted / everything else.
 * (This goes the **opposite way** from `applications`, where srv's third component is a
 * different axis and has to be dropped. Same class of trap, two different resolutions, so
 * each is written out in full rather than sharing one note.)
 * 2. **System vs self-built is decided by `importedChecklist`** (an indexed boolean), set at
 * creation from `template`: a templated checklist is pre-filled from RegDesk's regulatory
 * library, a self-built one starts empty.
 */
export const checklistsMetrics = defineMetrics([
  {
    id: "total_checklists",
    name: "Total Checklists",
    description: "Total number of DCT Checklists.",
    logic: "Count all Checklist records.",
    source: "productChecklist.countDocuments()",
    domain: "checklists",
    srvModule: "checklists",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["line", "bar"],
    coveredByV1: false,
    xlsxRow: 54,
  },
  {
    id: "custom_checklists",
    name: "Custom Checklists",
    description: "Number of user-created Checklists.",
    logic: "Count Checklists created by users.",
    source: "productChecklist(importedChecklist != true) — built by the customer, not from a template",
    domain: "checklists",
    srvModule: "checklists",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["pie", "bar"],
    coveredByV1: false,
    xlsxRow: 55,
  },
  {
    id: "system_checklists",
    name: "System Checklists",
    description: "Number of system-generated Checklists.",
    logic: "Count system-provided Checklists.",
    source: "productChecklist(importedChecklist = true) — pre-filled from a RegDesk template",
    domain: "checklists",
    srvModule: "checklists",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["pie", "bar"],
    coveredByV1: false,
    xlsxRow: 56,
  },
  {
    id: "cloned_checklists",
    name: "Cloned Checklists",
    description: "Number of cloned Checklists.",
    logic: "Count Checklists created through cloning.",
    source: "productChecklist.cloneRef exists and is non-null",
    domain: "checklists",
    srvModule: "checklists",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["pie", "bar"],
    coveredByV1: false,
    xlsxRow: 57,
  },
  {
    id: "modified_system_checklists",
    name: "Modified System Checklists",
    description: "Number of system Checklists modified by users.",
    logic: "Count system Checklists with user modifications.",
    source: "imported checklists with a Requirement Add/Remove entry in auditTrail(module=DCT)",
    domain: "checklists",
    srvModule: "checklists",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["bar"],
    coveredByV1: false,
    caveat:
      "Counts imported checklists that have at least one Requirement Add or Requirement Remove entry in the audit trail. Document uploads and approvals are excluded on purpose: those are progress through a checklist, not a change to it, and counting them would mark every system checklist anyone has used as modified. The count is a snapshot over all history, not restricted to the selected window.",
    xlsxRow: 58,
  },
  {
    id: "checklist_country_association",
    name: "Checklist Country Association",
    description: "Countries linked to Checklists.",
    logic: "Retrieve associated country records for each Checklist.",
    source: "productChecklist.countryId, the raw alpha-3 code",
    domain: "checklists",
    srvModule: "checklists",
    priority: "must",
    valueType: "count",
    dims: ["country"],
    rollup: "last",
    defaultWidget: "hbar",
    altWidgets: ["map", "table", "bar"],
    coveredByV1: false,
    xlsxRow: 59,
  },
  {
    id: "checklist_status",
    name: "Checklist Status",
    description: "Current status of a Checklist.",
    logic: "Retrieve current workflow status.",
    source: "derived from the approved and docsSubmitted booleans — there is no status field",
    domain: "checklists",
    srvModule: "checklists",
    priority: "must",
    valueType: "count",
    dims: ["class"],
    rollup: "last",
    defaultWidget: "pie",
    altWidgets: ["stacked-bar", "hbar", "table"],
    coveredByV1: false,
    caveat:
      "ProductChecklist has no status field. The three classes are derived from srv's own checklist dashboard, whose Approved and Submitted widgets are nested rather than disjoint, so submitted checklists are subtracted out of approved to keep the pie slices summing to the total.",
    xlsxRow: 60,
  },
])
